import { Key, KAPLAYCtx } from "kaplay";
import { SHAFT_LEFT, SHAFT_WIDTH } from "../../constants";
import { COLORS } from "../../colors";
import { PALETTE, textStyle } from "../../theme";
import { PrimaryColor } from "../../types";

const COLOR_BUTTONS: readonly { color: PrimaryColor; keys: Key[] }[] = [
    { color: "red", keys: ["1", "r"] },
    { color: "blue", keys: ["2", "b"] },
    { color: "yellow", keys: ["3", "y"] },
];

const BUTTON_Y = 885;
const PRESSED_SCALE = 0.92;
const IDLE_GLOW_OPACITY = 0.15;
const PRESSED_GLOW_OPACITY = 0.5;

/**
 * The three primary colour buttons. Taps and key presses both call `onPress`,
 * so touch and keyboard always behave the same.
 */
export function createColorButtons(k: KAPLAYCtx, onPress: (color: PrimaryColor) => void) {
    const slotWidth = SHAFT_WIDTH / COLOR_BUTTONS.length;

    const buttons = COLOR_BUTTONS.map(({ color, keys }, index) => {
        const x = SHAFT_LEFT + slotWidth * (index + 0.5);
        const rgb = COLORS[color].rgb;

        const glow = k.add([
            k.rect(130, 80, { radius: 12 }),
            k.pos(x, BUTTON_Y),
            k.anchor("center"),
            k.color(...rgb),
            k.opacity(IDLE_GLOW_OPACITY),
            k.z(14),
        ]);

        const body = k.add([
            k.rect(120, 72, { radius: 10 }),
            k.pos(x, BUTTON_Y),
            k.anchor("center"),
            k.color(...rgb),
            k.outline(2, k.rgb(...PALETTE.white)),
            k.area(),
            k.scale(1),
            k.z(15),
        ]);

        k.add([
            k.text(COLORS[color].name, textStyle(19)),
            k.pos(x, BUTTON_Y - 11),
            k.anchor("center"),
            k.color(...PALETTE.buttonText),
            k.z(16),
        ]);

        k.add([
            k.text(keys.join(" / ").toUpperCase(), textStyle(11)),
            k.pos(x, BUTTON_Y + 12),
            k.anchor("center"),
            k.color(40, 40, 50),
            k.z(16),
        ]);

        body.onClick(() => onPress(color));
        k.onKeyPress(keys, () => onPress(color));

        return { color, body, glow };
    });

    return {
        animatePress(color: PrimaryColor) {
            const button = buttons.find((b) => b.color === color);
            if (!button) return;
            button.body.scale = k.vec2(PRESSED_SCALE);
            button.glow.opacity = PRESSED_GLOW_OPACITY;
        },
        easeBack(dt: number) {
            for (const { body, glow } of buttons) {
                body.scale = k.vec2(k.lerp(body.scale.x, 1, dt * 12));
                glow.opacity = k.lerp(glow.opacity, IDLE_GLOW_OPACITY, dt * 8);
            }
        },
    };
}
